import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { PAGE_SIZE } from '@/api/common.ts';

// The parameters with the changes applied: a value sets its key, null or an empty string removes it.
export const withSearchParams = (current: URLSearchParams, changes: Record<string, string | null>) => {
    const next = new URLSearchParams(current);
    for (const [key, value] of Object.entries(changes)) {
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
    }
    return next;
};

// A single URL query parameter as state, so filters survive reloads and can be bookmarked or shared.
// Replaces the history entry, so typing into a search field doesn't flood the back button.
const useSearchParam = (key: string) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const setValue = (value: string | null) =>
        setSearchParams((current) => withSearchParams(current, { [key]: value }), { replace: true });

    return [searchParams.get(key), setValue] as const;
};

export default useSearchParam;

// A list's filter in the URL: changing it goes back to the list's first page.
export const useFilterParam = (key: string) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const setValue = (value: string | null) =>
        setSearchParams((current) => withSearchParams(current, { [key]: value, page: null }), { replace: true });

    return [searchParams.get(key), setValue] as const;
};

// Which of a page's dialogs is open, in the URL (`?dialog=new-client`, plan/docs/structure.md "Dialogs"), so back closes
// it and a reload or a link opens it again. Closing settles once the page is back without it, so going on to e.g. what
// the dialog created starts from there, and back from that page doesn't open the dialog again.
export const useDialogParam = <T extends string>(...names: T[]) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const dialog = names.find((name) => name === searchParams.get('dialog')) ?? null;
    // The row of a list the dialog is about (`&id=`), e.g. the rule it edits or the connection it picks from.
    const dialogId = dialog === null ? null : searchParams.get('id');

    // One opened from another takes its place in the history, so back and closing leave both.
    const openDialog = (name: T, id: string | null = null) =>
        searchParams.has('dialog')
            ? setSearchParams((current) => withSearchParams(current, { dialog: name, id }), {
                  replace: true,
                  state: location.state,
              })
            : setSearchParams((current) => withSearchParams(current, { dialog: name, id }), {
                  state: { isDialogOpened: true },
              });

    // Opened here, it goes back over the history entry that opening added; opened from a link, there's none to go back to.
    // Whether it's still open is read from the browser rather than this render: a dialog's confirm button, e.g., closes
    // it after its action, which may have gone on to another page already.
    const closeDialog = async () => {
        if (dialog === null || new URLSearchParams(window.location.search).get('dialog') !== dialog) return;
        if (location.state?.isDialogOpened) await navigate(-1);
        else
            await navigate(
                { search: `?${withSearchParams(searchParams, { dialog: null, id: null })}` },
                { replace: true }
            );
    };

    return { dialog, dialogId, openDialog, closeDialog };
};

// Whether any of a list's filters is set, from their values as read (an unknown one in the URL counts as not set).
export const hasFilterValues = (values: Record<string, string | null>) =>
    Object.values(values).some((value) => value !== null);

// What a list its search and filters leave empty offers (NoResults), from their values as read, `search` being the
// search (`?q=`): dropping the search, dropping the filters while keeping the search, or both.
export const useFilterResets = (values: { search: string | null } & Record<string, string | null>) => {
    const [, setSearchParams] = useSearchParams();
    const { search, ...filters } = values;

    return {
        hasSearch: search !== null,
        filterCount: Object.values(filters).filter((value) => value !== null).length,
        onRemoveSearch: () =>
            setSearchParams((current) => withSearchParams(current, { q: null, page: null }), { replace: true }),
        onRemoveFilters: () => setSearchParams(search === null ? {} : { q: search }, { replace: true }),
        onReset: () => setSearchParams({}, { replace: true }),
    };
};

// A list's page in the URL (`?page=`, or another key where a page has several lists), from 1, and the link to another
// one. The first page leaves the parameter out.
export const usePageParam = (key = 'page') => {
    const [searchParams] = useSearchParams();
    const page = Math.max(1, Number(searchParams.get(key)) || 1);
    const getPageHref = (target: number) =>
        `?${withSearchParams(searchParams, { [key]: target > 1 ? String(target) : null })}`;
    return { page, getPageHref };
};

// How many pages a list's `count` fills.
export const getPageCount = (count: number, pageSize = PAGE_SIZE) => Math.ceil(count / pageSize);
